import { io, Socket } from 'socket.io-client'
import { Transaction, TransactionEvent } from '../types/transaction'
import { Customer } from '../types/customers'
import { Product } from '../types/product'

export interface TransactionUpdate extends Transaction {
  events?: TransactionEvent[]
}

export interface TransactionCreated extends Transaction {
  events?: TransactionEvent[]
}

export interface TransactionStatusChanged {
  transactionId: string // Transaction ID as string
  status: string
  timestamp: string
}

export interface CustomerCreated {
  customer: Customer
  timestamp: string
}

export interface CustomerUpdated {
  customer: Customer
  timestamp: string
}

export interface CustomerDeleted {
  customerId: string
  timestamp: string
}

export interface ProductCreated {
  product: Product
  timestamp: string
}

export interface ProductUpdated {
  product: Product
  timestamp: string
}

export interface ProductDeleted {
  productId: string
  timestamp: string
}

// Provider verification events
export interface VerificationStarted {
  providerId: string
  timestamp: string
}

export interface VerificationProgress {
  providerId: string
  step: string
  message: string
  timestamp: string
}

export interface VerificationCompleted {
  providerId: string
  success: boolean
  result: {
    message?: string
    requirements?: unknown
    verificationPhase?: string
    error?: string
    updateWarnings?: string[]
  }
  timestamp: string
}

/** Generic notification data from websocket */
export interface NotificationData {
  id: string
  type: string
  title?: string
  message?: string
  timestamp: string
  read?: boolean
  metadata?: Record<string, unknown>
}

export interface SocketConnectionStatus {
  connected: boolean
  userId?: string
  timestamp?: string
}

interface GenericListener {
  event: string
  handler: (...args: unknown[]) => void
}

class SocketService {
  private socket: Socket | null = null
  private currentToken: string | null = null
  private pingInterval: NodeJS.Timeout | null = null
  private authFailed = false

  // Generic listeners registered via on() — re-attached on reconnect
  private genericListeners: GenericListener[] = []

  // Typed event listeners (legacy — kept for backward compatibility during migration)
  private transactionUpdateListeners: ((data: TransactionUpdate) => void)[] = []
  private transactionCreatedListeners: ((data: TransactionCreated) => void)[] = []
  private transactionStatusChangedListeners: ((data: TransactionStatusChanged) => void)[] = []
  private connectionStatusListeners: ((status: SocketConnectionStatus) => void)[] = []
  private notificationListeners: ((data: NotificationData) => void)[] = []
  private notificationCountUpdateListeners: ((data: { count: number }) => void)[] = []
  private customerCreatedListeners: ((data: CustomerCreated) => void)[] = []
  private customerUpdatedListeners: ((data: CustomerUpdated) => void)[] = []
  private customerDeletedListeners: ((data: CustomerDeleted) => void)[] = []
  private productCreatedListeners: ((data: ProductCreated) => void)[] = []
  private productUpdatedListeners: ((data: ProductUpdated) => void)[] = []
  private productDeletedListeners: ((data: ProductDeleted) => void)[] = []
  private verificationStartedListeners: ((data: VerificationStarted) => void)[] = []
  private verificationProgressListeners: ((data: VerificationProgress) => void)[] = []
  private verificationCompletedListeners: ((data: VerificationCompleted) => void)[] = []

  /**
   * Connect to the Socket.IO server with a platform-user JWT (provider /
   * operation surfaces). Delegates to {@link connectWithAuth}, keyed by the
   * token so re-calls dedupe correctly.
   */
  connect(token: string): void {
    this.connectWithAuth(token, { token })
  }

  /**
   * Connect to the Socket.IO server with an arbitrary handshake auth payload.
   * Principal-agnostic so different surfaces can present different credentials:
   * platform users pass `{ token }` (JWT); the customer hosted portal, which
   * has no platform JWT, passes `{ sessionId }` (the same opaque credential it
   * already uses for its REST reads). `key` is the dedupe/identity key used to
   * decide whether to reuse or rebuild the socket.
   *
   * Safe to call multiple times -- it reuses the existing socket when `key` is
   * unchanged, or tears down and rebuilds when it changes. Connection errors
   * are handled internally via status listeners; this method never throws.
   */
  connectWithAuth(key: string, auth: Record<string, unknown>): void {
    // Already connected with the same identity -- nothing to do
    if (this.socket?.connected && this.currentToken === key) {
      return
    }

    // Socket exists but identity changed -- tear down and recreate
    if (this.socket && this.currentToken !== key) {
      this.destroySocket()
    }

    // Socket already exists (same identity, just not connected yet) -- let
    // Socket.IO's built-in reconnection handle it
    if (this.socket) {
      if (this.socket.disconnected) {
        this.socket.connect()
      }
      return
    }

    this.currentToken = key
    this.authFailed = false

    // The socket.io server lives in the notification-service, not the REST API.
    // In deployed envs the API edge upgrades `${API_HOST}/socket.io` to that
    // service, so the API base (minus `/v1`) is the correct origin. Local dev
    // has no edge, so we target the notification-service port directly — either via
    // an explicit NEXT_PUBLIC_REALTIME_URL or the :3009 default.
    const realtimeUrl =
      process.env.NEXT_PUBLIC_REALTIME_URL ||
      process.env.NEXT_PUBLIC_API_URL?.replace('/v1', '') ||
      'http://localhost:3009'

    // Create the socket with autoConnect: false so we can attach all
    // handlers before the first connection attempt.
    this.socket = io(`${realtimeUrl}/transactions`, {
      auth,
      transports: ['websocket', 'polling'],
      timeout: 20000,
      autoConnect: false,
      // Let Socket.IO handle all reconnection -- no manual logic needed
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    })

    this.attachEventHandlers()
    this.socket.connect()
  }

  disconnect(): void {
    this.destroySocket()
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }

  // --- Private helpers ---------------------------------------------------

  private destroySocket(): void {
    this.clearPingInterval()
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
    this.currentToken = null
    this.notifyConnectionStatus({ connected: false })
  }

  private attachEventHandlers(): void {
    if (!this.socket) return

    // Connection lifecycle
    this.socket.on('connect', () => {
      this.notifyConnectionStatus({ connected: true })
      this.startPingInterval()
    })

    this.socket.on('connected', (data: { userId: string; timestamp: string }) => {
      this.notifyConnectionStatus({
        connected: true,
        userId: data.userId,
        timestamp: data.timestamp,
      })
    })

    this.socket.on('auth_error', (data: { message?: string }) => {
      console.error('[SocketService] Authentication rejected by server:', data?.message)
      this.authFailed = true
      this.destroySocket()
    })

    this.socket.on('disconnect', reason => {
      this.clearPingInterval()
      this.notifyConnectionStatus({ connected: false })

      if (reason === 'io server disconnect' && !this.authFailed) {
        this.socket?.connect()
      }
    })

    this.socket.on('connect_error', error => {
      console.warn('[SocketService] Connection error (will retry):', error.message)
      this.notifyConnectionStatus({ connected: false })
    })

    // Transaction events
    this.socket.on('transaction:update', (data: TransactionUpdate) => {
      this.transactionUpdateListeners.forEach(listener => listener(data))
    })

    this.socket.on('transaction:created', (data: TransactionCreated) => {
      this.transactionCreatedListeners.forEach(listener => listener(data))
    })

    this.socket.on('transaction:status-changed', (data: TransactionStatusChanged) => {
      this.transactionStatusChangedListeners.forEach(listener => listener(data))
    })

    // Notification events
    this.socket.on('notification:new', (data: NotificationData) => {
      this.notificationListeners.forEach(listener => listener(data))
    })

    this.socket.on('notification:count-update', (data: { count: number }) => {
      this.notificationCountUpdateListeners.forEach(listener => listener(data))
    })

    // Customer events
    this.socket.on('customer:created', (data: CustomerCreated) => {
      this.customerCreatedListeners.forEach(listener => listener(data))
    })

    this.socket.on('customer:updated', (data: CustomerUpdated) => {
      this.customerUpdatedListeners.forEach(listener => listener(data))
    })

    this.socket.on('customer:deleted', (data: CustomerDeleted) => {
      this.customerDeletedListeners.forEach(listener => listener(data))
    })

    // Product events
    this.socket.on('product:created', (data: ProductCreated) => {
      this.productCreatedListeners.forEach(listener => listener(data))
    })

    this.socket.on('product:updated', (data: ProductUpdated) => {
      this.productUpdatedListeners.forEach(listener => listener(data))
    })

    this.socket.on('product:deleted', (data: ProductDeleted) => {
      this.productDeletedListeners.forEach(listener => listener(data))
    })

    // Verification events
    this.socket.on('verification:started', (data: VerificationStarted) => {
      this.verificationStartedListeners.forEach(listener => listener(data))
    })

    this.socket.on('verification:progress', (data: VerificationProgress) => {
      this.verificationProgressListeners.forEach(listener => listener(data))
    })

    this.socket.on('verification:completed', (data: VerificationCompleted) => {
      this.verificationCompletedListeners.forEach(listener => listener(data))
    })

    // Heartbeat response -- just absorb it
    this.socket.on('pong', () => {})

    // Re-attach any generic listeners registered before connect()
    for (const { event, handler } of this.genericListeners) {
      this.socket.on(event, handler)
    }
  }

  private startPingInterval(): void {
    this.clearPingInterval()
    this.pingInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping')
      }
    }, 30000)
  }

  private clearPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
  }

  // --- Generic event subscription -----------------------------------------

  /**
   * Subscribe to any socket event by name. Listeners registered before
   * connect() are stored and automatically attached once the socket exists.
   * Returns an unsubscribe function.
   */
  on<T = unknown>(event: string, handler: (data: T) => void): () => void {
    const socketHandler = (...args: unknown[]) => {
      handler(args[0] as T)
    }
    const entry: GenericListener = { event, handler: socketHandler }
    this.genericListeners.push(entry)

    this.socket?.on(event, socketHandler)

    return () => {
      this.socket?.off(event, socketHandler)
      const idx = this.genericListeners.indexOf(entry)
      if (idx > -1) this.genericListeners.splice(idx, 1)
    }
  }

  // --- Typed event listener registration (legacy) ------------------------

  onTransactionUpdate(listener: (data: TransactionUpdate) => void): () => void {
    this.transactionUpdateListeners.push(listener)
    return () => {
      const index = this.transactionUpdateListeners.indexOf(listener)
      if (index > -1) this.transactionUpdateListeners.splice(index, 1)
    }
  }

  onTransactionCreated(listener: (data: TransactionCreated) => void): () => void {
    this.transactionCreatedListeners.push(listener)
    return () => {
      const index = this.transactionCreatedListeners.indexOf(listener)
      if (index > -1) this.transactionCreatedListeners.splice(index, 1)
    }
  }

  onTransactionStatusChanged(listener: (data: TransactionStatusChanged) => void): () => void {
    this.transactionStatusChangedListeners.push(listener)
    return () => {
      const index = this.transactionStatusChangedListeners.indexOf(listener)
      if (index > -1) this.transactionStatusChangedListeners.splice(index, 1)
    }
  }

  onConnectionStatus(listener: (status: SocketConnectionStatus) => void): () => void {
    this.connectionStatusListeners.push(listener)
    return () => {
      const index = this.connectionStatusListeners.indexOf(listener)
      if (index > -1) this.connectionStatusListeners.splice(index, 1)
    }
  }

  onNotification(listener: (data: NotificationData) => void): () => void {
    this.notificationListeners.push(listener)
    return () => {
      const index = this.notificationListeners.indexOf(listener)
      if (index > -1) this.notificationListeners.splice(index, 1)
    }
  }

  onNotificationCountUpdate(listener: (data: { count: number }) => void): () => void {
    this.notificationCountUpdateListeners.push(listener)
    return () => {
      const index = this.notificationCountUpdateListeners.indexOf(listener)
      if (index > -1) this.notificationCountUpdateListeners.splice(index, 1)
    }
  }

  onCustomerCreated(listener: (data: CustomerCreated) => void): () => void {
    this.customerCreatedListeners.push(listener)
    return () => {
      const index = this.customerCreatedListeners.indexOf(listener)
      if (index > -1) this.customerCreatedListeners.splice(index, 1)
    }
  }

  onCustomerUpdated(listener: (data: CustomerUpdated) => void): () => void {
    this.customerUpdatedListeners.push(listener)
    return () => {
      const index = this.customerUpdatedListeners.indexOf(listener)
      if (index > -1) this.customerUpdatedListeners.splice(index, 1)
    }
  }

  onCustomerDeleted(listener: (data: CustomerDeleted) => void): () => void {
    this.customerDeletedListeners.push(listener)
    return () => {
      const index = this.customerDeletedListeners.indexOf(listener)
      if (index > -1) this.customerDeletedListeners.splice(index, 1)
    }
  }

  onProductCreated(listener: (data: ProductCreated) => void): () => void {
    this.productCreatedListeners.push(listener)
    return () => {
      const index = this.productCreatedListeners.indexOf(listener)
      if (index > -1) this.productCreatedListeners.splice(index, 1)
    }
  }

  onProductUpdated(listener: (data: ProductUpdated) => void): () => void {
    this.productUpdatedListeners.push(listener)
    return () => {
      const index = this.productUpdatedListeners.indexOf(listener)
      if (index > -1) this.productUpdatedListeners.splice(index, 1)
    }
  }

  onProductDeleted(listener: (data: ProductDeleted) => void): () => void {
    this.productDeletedListeners.push(listener)
    return () => {
      const index = this.productDeletedListeners.indexOf(listener)
      if (index > -1) this.productDeletedListeners.splice(index, 1)
    }
  }

  onVerificationStarted(listener: (data: VerificationStarted) => void): () => void {
    this.verificationStartedListeners.push(listener)
    return () => {
      const index = this.verificationStartedListeners.indexOf(listener)
      if (index > -1) this.verificationStartedListeners.splice(index, 1)
    }
  }

  onVerificationProgress(listener: (data: VerificationProgress) => void): () => void {
    this.verificationProgressListeners.push(listener)
    return () => {
      const index = this.verificationProgressListeners.indexOf(listener)
      if (index > -1) this.verificationProgressListeners.splice(index, 1)
    }
  }

  onVerificationCompleted(listener: (data: VerificationCompleted) => void): () => void {
    this.verificationCompletedListeners.push(listener)
    return () => {
      const index = this.verificationCompletedListeners.indexOf(listener)
      if (index > -1) this.verificationCompletedListeners.splice(index, 1)
    }
  }

  private notifyConnectionStatus(status: SocketConnectionStatus): void {
    this.connectionStatusListeners.forEach(listener => listener(status))
  }
}

// Export singleton instance
export const socketService = new SocketService()
